import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { supabase } from "@/lib/supabaseClient";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};

// Hook Vision simplifié avec meilleure gestion des erreurs
function useVisionSync() {
  const [isReady, setIsReady] = useState(false);
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const readyFiredRef = useRef(false);

  const markReady = () => {
    if (readyFiredRef.current) return;
    readyFiredRef.current = true;
    if (mountedRef.current) setIsReady(true);
  };

  useEffect(() => {
    mountedRef.current = true;
    const VISION_ORIGIN = "https://vision.artkiv.com";
    const VISION_PATH = "/auth/sync-bridge";
    const VISION_TIMEOUT = 3000;

    // Ne pas lancer la sync si on est soi-même chargé dans un iframe (ex: bridge de Vision)
    if (window !== window.parent) {
      markReady();
      return;
    }

    console.log("[Vision] Tentative de connexion...");

    // Timeout de sécurité
    timeoutRef.current = setTimeout(() => {
      console.warn("[Vision] ⚠️ Timeout - Continue sans Vision");
      markReady();
    }, VISION_TIMEOUT);

    // Créer iframe avec gestion d'erreurs
    const createIframe = () => {
      try {
        const iframe = document.createElement("iframe");
        iframe.id = "vision-sync-iframe";
        iframe.src = `${VISION_ORIGIN}${VISION_PATH}`;
        iframe.style.cssText = "position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;left:-9999px;";
        iframe.setAttribute("sandbox", "allow-scripts allow-same-origin");

        iframe.onload = () => {
          console.log("[Vision] ✅ Iframe chargée");
          clearTimeout(timeoutRef.current);
          markReady();
        };

        iframe.onerror = () => {
          console.warn("[Vision] ❌ Erreur de chargement");
          clearTimeout(timeoutRef.current);
          markReady();
        };

        document.body.appendChild(iframe);
        iframeRef.current = iframe;
      } catch (e) {
        console.error("[Vision] ❌ Erreur création iframe:", e);
        clearTimeout(timeoutRef.current);
        markReady();
      }
    };

    createIframe();

    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (iframeRef.current?.parentNode) {
        try {
          iframeRef.current.parentNode.removeChild(iframeRef.current);
        } catch (_) {}
      }
    };
  }, []);

  const pullSessionFromVision = useCallback(async () => {
    if (!iframeRef.current?.contentWindow) {
      console.log("[Vision] Pas d'iframe disponible");
      return false;
    }

    if (!mountedRef.current) return false;

    return new Promise((resolve) => {
      const requestId = `${Date.now()}_${Math.random().toString(16).slice(2)}`;

      const timeout = setTimeout(() => {
        window.removeEventListener("message", handler);
        console.warn("[Vision] Timeout pull session");
        resolve(false);
      }, 1500);

      const handler = (event) => {
        if (event.origin !== "https://vision.artkiv.com") return;
        if (
          event.data?.type === "AUTH_SYNC:SESSION_RESPONSE" &&
          event.data?.requestId === requestId
        ) {
          clearTimeout(timeout);
          window.removeEventListener("message", handler);

          const session = event.data.payload;
          if (session?.access_token) {
            console.log("[Vision] ✅ Session récupérée");
            resolve(session);
          } else {
            resolve(false);
          }
        }
      };

      window.addEventListener("message", handler);

      try {
        iframeRef.current.contentWindow.postMessage(
          { type: "AUTH_SYNC:GET_SESSION", requestId },
          "https://vision.artkiv.com"
        );
      } catch (e) {
        clearTimeout(timeout);
        window.removeEventListener("message", handler);
        console.error("[Vision] Erreur pull:", e);
        resolve(false);
      }
    });
  }, []);

  const notifyVisionLogout = useCallback(async () => {
    if (!iframeRef.current?.contentWindow) return;
    const requestId = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    try {
      iframeRef.current.contentWindow.postMessage(
        { type: "AUTH_SYNC:LOGOUT", requestId },
        "https://vision.artkiv.com"
      );
    } catch (_) {}
  }, []);

  return { isReady, pullSessionFromVision, notifyVisionLogout };
}

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState(null);

  const profileFetchedOnceRef = useRef(false);
  const initDoneRef = useRef(false);
  const inflightProfileRef = useRef(null);
  const lastProfileUserIdRef = useRef(null);
  const mountedRef = useRef(true);

  const { isReady: visionReady, pullSessionFromVision, notifyVisionLogout } = useVisionSync();

  // ✅ Gérer mountedRef au niveau du Provider
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Applique silencieusement les transferts de collection en attente (pending_signup)
  // pour les utilisateurs qui viennent de s'inscrire avec l'email cible.
  const applyPendingSignupTransfers = async (sess) => {
    try {
      const { data: pending } = await supabase
        .from("collection_transfer_requests")
        .select("token")
        .eq("to_email", sess.user.email)
        .eq("status", "pending_signup");

      if (!pending || pending.length === 0) return;

      for (const req of pending) {
        await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/accept-collection-transfer`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${sess.access_token}`,
              apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ token: req.token }),
          }
        );
      }
    } catch (e) {
      console.warn("[AuthContext] applyPendingSignupTransfers error:", e);
    }
  };

  const fetchUserProfile = useCallback(async () => {
    if (!mountedRef.current) return null;
    
    const uid = session?.user?.id;

    if (!uid) {
      setProfile(null);
      profileFetchedOnceRef.current = false;
      lastProfileUserIdRef.current = null;
      return null;
    }

    if (profileFetchedOnceRef.current && lastProfileUserIdRef.current === uid) {
      return profile;
    }

    if (inflightProfileRef.current) return inflightProfileRef.current;

    setProfileLoading(true);

    inflightProfileRef.current = (async () => {
      try {
        if (!mountedRef.current) return null;
        
        console.log("[AuthContext] 🔍 Fetch profile pour user:", uid);

        const { data, error: dbError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", uid)
          .limit(1);

        if (!mountedRef.current) return null;

        // Si erreur RLS, créer un profil fallback
        if (dbError) {
          console.warn("[AuthContext] ⚠️ RLS profiles bloqué:", dbError.message);
          
          const fallbackProfile = {
            id: uid,
            email: session?.user?.email || null,
            role: session?.user?.user_metadata?.role || session?.user?.app_metadata?.role || 'user',
            plan: session?.user?.user_metadata?.plan || session?.user?.app_metadata?.plan || 'standard',
            first_name: session?.user?.user_metadata?.first_name || null,
            last_name: session?.user?.user_metadata?.last_name || null,
            company: null,
            approved: true,
            stripe_subscription_status: 'active',
          };
          
          if (mountedRef.current) {
            console.log("[AuthContext] ✅ Profil fallback actif:", fallbackProfile);
            setProfile(fallbackProfile);
            lastProfileUserIdRef.current = uid;
            profileFetchedOnceRef.current = true;
          }
          return fallbackProfile;
        }

        const profileData = Array.isArray(data) && data.length > 0 ? data[0] : null;

        if (!profileData) {
          console.warn("[AuthContext] ⚠️ Profil DB vide, utilisation fallback");
          
          const fallbackProfile = {
            id: uid,
            email: session?.user?.email || null,
            role: session?.user?.user_metadata?.role || session?.user?.app_metadata?.role || 'user',
            plan: session?.user?.user_metadata?.plan || session?.user?.app_metadata?.plan || 'standard',
            first_name: session?.user?.user_metadata?.first_name || null,
            last_name: session?.user?.user_metadata?.last_name || null,
            approved: true,
            stripe_subscription_status: 'active',
          };
          
          if (mountedRef.current) {
            setProfile(fallbackProfile);
            lastProfileUserIdRef.current = uid;
            profileFetchedOnceRef.current = true;
          }
          return fallbackProfile;
        }

        if (mountedRef.current) {
          setProfile(profileData);
          lastProfileUserIdRef.current = uid;
          profileFetchedOnceRef.current = true;
          console.log("[AuthContext] ✅ Profil DB chargé:", profileData);
        }
        return profileData;
      } catch (e) {
        // AbortError silencieux
        if (e?.name === "AbortError" || e?.message?.includes("aborted")) {
          console.warn("[AuthContext] ⚠️ fetchProfile annulé (AbortError)");
          return null;
        }
        console.error("[AuthContext] ❌ Erreur catch, création profil minimal:", e);
        
        const emergencyProfile = {
          id: uid,
          email: session?.user?.email || null,
          role: 'user',
          plan: 'standard',
          approved: true,
          stripe_subscription_status: 'active',
        };
        
        if (mountedRef.current) {
          setProfile(emergencyProfile);
          lastProfileUserIdRef.current = uid;
          profileFetchedOnceRef.current = true;
        }
        return emergencyProfile;
      } finally {
        if (mountedRef.current) {
          setProfileLoading(false);
          inflightProfileRef.current = null;
        }
      }
    })();

    return inflightProfileRef.current;
  }, [session, profile]);

  // Init session avec tentative Vision
  useEffect(() => {
    if (initDoneRef.current) return;
    if (!visionReady) return;

    let subscription = null;

    const registerListener = () => {
      const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
        if (!mountedRef.current) return;

        console.log("[AuthContext] 🔄 Auth state change:", event);
        setSession(newSession);
        setUser(newSession?.user ?? null);

        const uid = newSession?.user?.id ?? null;

        if (!uid) {
          lastProfileUserIdRef.current = null;
          profileFetchedOnceRef.current = false;
          setProfile(null);
        } else if (uid !== lastProfileUserIdRef.current) {
          profileFetchedOnceRef.current = false;
        }

        if (event === "SIGNED_OUT") {
          lastProfileUserIdRef.current = null;
          profileFetchedOnceRef.current = false;
          setProfile(null);
        }

        if (event === "SIGNED_IN" && newSession?.user?.email) {
          applyPendingSignupTransfers(newSession);
        }
      });
      subscription = listener;
    };

    const init = async () => {
      try {
        if (!mountedRef.current) return;

        console.log("[AuthContext] 🚀 Initialisation...");

        const { data, error: sErr } = await supabase.auth.getSession();
        if (sErr) throw sErr;
        if (!mountedRef.current) return;

        if (data.session) {
          console.log("[AuthContext] ✅ Session locale trouvée");
          setSession(data.session);
          setUser(data.session.user ?? null);
        } else {
          console.log("[AuthContext] 🔄 Tentative pull Vision...");

          try {
            const visionSession = await pullSessionFromVision();

            if (!mountedRef.current) return;

            if (visionSession && visionSession.access_token) {
              const { error: setErr } = await supabase.auth.setSession({
                access_token: visionSession.access_token,
                refresh_token: visionSession.refresh_token,
              });

              if (!mountedRef.current) return;

              if (!setErr) {
                const { data: newData } = await supabase.auth.getSession();
                if (mountedRef.current) {
                  setSession(newData.session ?? null);
                  setUser(newData.session?.user ?? null);
                  console.log("[AuthContext] ✅ Session Vision importée");
                }
              } else {
                console.warn("[AuthContext] ⚠️ Échec import Vision");
                setSession(null);
                setUser(null);
              }
            } else {
              console.log("[AuthContext] ℹ️ Pas de session Vision");
              setSession(null);
              setUser(null);
            }
          } catch (visionError) {
            console.warn("[AuthContext] ⚠️ Vision non disponible:", visionError);
            if (mountedRef.current) {
              setSession(null);
              setUser(null);
            }
          }
        }

        initDoneRef.current = true;
        // Enregistrer le listener APRÈS getSession pour éviter la concurrence navigator.locks
        registerListener();
      } catch (e) {
        if (e?.name === "AbortError" || e?.message?.includes("aborted")) {
          console.warn("[AuthContext] ⚠️ Init annulée (AbortError) — session=null, listener enregistré");
          // Marquer comme fait pour éviter ré-entrée, session reste null
          initDoneRef.current = true;
          if (mountedRef.current) {
            setSession(null);
            setUser(null);
          }
          // Enregistrer le listener quand même pour capter les signIn
          registerListener();
        } else {
          console.error("[AuthContext] ❌ Erreur init:", e);
          if (mountedRef.current) {
            setError(e);
            initDoneRef.current = true;
          }
          registerListener();
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          console.log("[AuthContext] ✅ Initialisation terminée");
        }
      }
    };

    init();

    return () => {
      subscription?.subscription?.unsubscribe?.();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visionReady]);

  // Fetch profil once quand session est prête
  useEffect(() => {
    if (!initDoneRef.current || !mountedRef.current) return;

    if (session?.user?.id && !profileFetchedOnceRef.current) {
      fetchUserProfile();
    }

    if (!session?.user?.id) {
      setProfile(null);
      profileFetchedOnceRef.current = false;
      lastProfileUserIdRef.current = null;
    }
  }, [session?.user?.id, fetchUserProfile]);

  const isAuthenticated = !!user;

  const signUp = async ({ email, password, options }) => {
    const { data, error } = await supabase.auth.signUp({ email, password, options });
    if (error) throw error;
    if (mountedRef.current) {
      setSession(data.session ?? null);
      setUser(data.user ?? null);
    }
    return { data, error: null, user: data.user ?? null, session: data.session ?? null };
  };

  const signIn = async ({ email, password }) => {
    const attempt = async () => {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return data;
    };

    const isAbort = (e) => e?.name === "AbortError" || e?.message?.includes("aborted") || e?.message?.includes("signal");

    let data;
    try {
      data = await attempt();
    } catch (err1) {
      if (!isAbort(err1)) throw err1;
      console.warn("[AuthContext] signIn AbortError #1 — retry dans 1000ms");
      await new Promise((r) => setTimeout(r, 1000));
      try {
        data = await attempt();
      } catch (err2) {
        if (!isAbort(err2)) throw err2;
        console.warn("[AuthContext] signIn AbortError #2 — retry final dans 1500ms");
        await new Promise((r) => setTimeout(r, 1500));
        data = await attempt();
      }
    }

    if (mountedRef.current) {
      setSession(data.session ?? null);
      setUser(data.user ?? null);
      profileFetchedOnceRef.current = false;
    }
    return data;
  };

  const signOut = useCallback(async () => {
    try {
      await notifyVisionLogout();
      await supabase.auth.signOut();

      try {
        localStorage.removeItem("profile");
        localStorage.removeItem("user_role");
        localStorage.removeItem("delegated_context");
        sessionStorage.clear();
      } catch (_) {}

      if (mountedRef.current) {
        setSession(null);
        setUser(null);
        setProfile(null);
        profileFetchedOnceRef.current = false;
        lastProfileUserIdRef.current = null;
      }
    } catch (error) {
      console.error("[AuthContext] ❌ Erreur signOut:", error);
      if (mountedRef.current) {
        setSession(null);
        setUser(null);
        setProfile(null);
      }
    }
  }, [notifyVisionLogout]);

  const globalSignOut = useCallback(async () => {
    await signOut();
    window.location.href = "/";
  }, [signOut]);

  const value = useMemo(
    () => ({
      session,
      user,
      profile,
      loading,
      profileLoading,
      profileFetchedOnce: profileFetchedOnceRef.current,
      error,
      isAuthenticated,
      fetchUserProfile,
      signUp,
      signIn,
      signOut,
      globalSignOut,
    }),
    [session, user, profile, loading, profileLoading, error, isAuthenticated, fetchUserProfile, signOut, globalSignOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;